import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { Provider } from 'react-redux';
import store from './app/client-redux/store';
import axiosConfig from './libraries/axiosConfig';
import * as Sentry from '@sentry/react';
import { BrowserRouter, createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import { ColorSchemeScript, MantineProvider, type MantineThemeOverride } from '@mantine/core';
import { ContextMenuProvider } from 'mantine-contextmenu';
import { GoogleMapsProvider } from './app/googleMapsProvider';
import { QueryClient, QueryClientProvider } from 'react-query';
import { StatusOperDialogProvider } from './app/context/status-oper-dialog';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import 'mantine-contextmenu/styles.layer.css';
import './app.scss';

axiosConfig();

/**
 * IMPORTANT: Mantine v9 requires EXACTLY 10 colors in a tuple
 */
const zarphColors = [
  '#003f52',
  '#004960',
  '#016273',
  '#47d4ff',
  '#03abac',
  '#176d88',
  '#3b3b3b',
  '#2e2e2e',
  '#303030',
  '#fcfcfc'
] as const;

const themeOverride: MantineThemeOverride = {
  colors: {
    zarph: zarphColors
  },
  breakpoints: {
    xs: '36em',
    sm: '48em',
    md: '62em',
    lg: '75em',
    xl: '87.5em'
  }
};

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: process.env.NODE_ENV,

  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    })
  ],

  release: process.env.REACT_APP_VERSION,
  tracesSampleRate: Number(process.env.REACT_APP_SENTRY_TRACE_SAMPLE_RATE)
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false
    }
  }
});

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element not found');
}
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <ColorSchemeScript />
    <MantineProvider theme={themeOverride}>
      <ContextMenuProvider>
        <Provider store={store}>
          <GoogleMapsProvider>
            <QueryClientProvider client={queryClient}>
              <BrowserRouter
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true
                }}
              >
                <StatusOperDialogProvider>
                  <App />
                </StatusOperDialogProvider>
              </BrowserRouter>
            </QueryClientProvider>
          </GoogleMapsProvider>
        </Provider>
      </ContextMenuProvider>
    </MantineProvider>
  </React.StrictMode>
);

reportWebVitals();
